import { useEffect, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import {
  InputAdornment,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  ToolbarButton,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { useLocalization } from '@/hooks/useLocalization';

interface GridQuickSearchProps {
  touchFriendly?: boolean;
}

const SearchContainer = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

interface SearchAnimationProps {
  expanded: boolean;
  reduceMotion: boolean;
}

const SearchControl = styled(TextField, {
  shouldForwardProp: (property) =>
    property !== 'expanded' && property !== 'reduceMotion',
})<SearchAnimationProps>(({ expanded, reduceMotion, theme }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: expanded ? 200 : 'var(--trigger-width)',
  opacity: expanded ? 1 : 0,
  pointerEvents: expanded ? 'auto' : 'none',
  transition: reduceMotion
    ? 'none'
    : theme.transitions.create(['width', 'opacity'], { duration: 220 }),
}));

/**
 * The expanding quick-filter search box shared by the accounts grid toolbars
 * (the advisor search/export toolbar and the default toolbar).
 */
export function GridQuickSearch({
  touchFriendly = false,
}: GridQuickSearchProps) {
  const { t } = useLocalization();
  const apiRef = useGridApiContext();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Expansion is controlled rather than seeded once with `defaultExpanded`: the
  // quick filter can be set from outside the toolbar (the owning page syncing
  // the grid to its `?search=` params), and a collapsed search box would hide
  // the fact that rows are being filtered.
  const [searchExpanded, setSearchExpanded] = useState(() =>
    Boolean(apiRef.current.state.filter.filterModel.quickFilterValues?.length),
  );
  useEffect(() => {
    return apiRef.current.subscribeEvent('filterModelChange', (model) => {
      if (model.quickFilterValues?.length) setSearchExpanded(true);
    });
  }, [apiRef]);
  const searchLabel = t('grid.search', 'Search');
  const clearSearchLabel = t('clearSearch', 'Clear search');
  const touchTargetStyles = touchFriendly
    ? { minHeight: 44, minWidth: 44 }
    : undefined;

  return (
    <SearchContainer
      expanded={searchExpanded}
      onExpandedChange={setSearchExpanded}
    >
      <QuickFilterTrigger
        render={(
          { material: _buttonMaterial, ref: triggerRef, ...triggerProps },
          state,
        ) => (
          <Tooltip
            title={searchLabel}
            enterDelay={0}
            arrow
          >
            <ToolbarButton
              {...triggerProps}
              ref={(element) => {
                if (typeof triggerRef === 'function') {
                  triggerRef(element);
                }
              }}
              aria-label={searchLabel}
              aria-disabled={state.expanded}
              style={{
                ...touchTargetStyles,
                gridArea: '1 / 1',
                width: 'min-content',
                height: 'min-content',
                zIndex: 1,
                opacity: state.expanded ? 0 : 1,
                pointerEvents: state.expanded ? 'none' : 'auto',
                transition: reduceMotion ? 'none' : 'opacity 220ms ease',
              }}
            >
              <SearchIcon fontSize='small' />
            </ToolbarButton>
          </Tooltip>
        )}
      />
      <QuickFilterControl
        render={(
          { ref, slotProps: controlSlotProps, ...controlProps },
          state,
        ) => (
          <SearchControl
            {...controlProps}
            expanded={state.expanded}
            reduceMotion={reduceMotion}
            inputRef={ref}
            aria-label={searchLabel}
            placeholder={searchLabel}
            size='small'
            data-testid='accounts-grid-search-control'
            slotProps={{
              ...controlSlotProps,
              htmlInput: {
                ...controlSlotProps?.htmlInput,
                'aria-label': searchLabel,
              },
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
                endAdornment: state.value ? (
                  <InputAdornment position='end'>
                    <QuickFilterClear
                      edge='end'
                      size='small'
                      aria-label={clearSearchLabel}
                      style={touchTargetStyles}
                    >
                      <CancelIcon fontSize='small' />
                    </QuickFilterClear>
                  </InputAdornment>
                ) : null,
                ...controlSlotProps?.input,
              },
            }}
          />
        )}
      />
    </SearchContainer>
  );
}
